import type { LucideIcon } from "lucide-react";
import { AlertCircle, CheckCircle2, CircleDashed, Info, RefreshCw, TriangleAlert } from "lucide-react";
import { useEffect } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { reloadIfNewBuild } from "../lib/build-freshness";

export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {action ? <div className="page-header__action">{action}</div> : null}
    </header>
  );
}

export function Button({
  variant = "primary",
  icon: Icon,
  children,
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "quiet" | "danger";
  icon?: LucideIcon;
}) {
  return (
    <button
      className={`button button--${variant} ${className}`.trim()}
      type="button"
      {...props}
    >
      {Icon ? <Icon aria-hidden="true" size={17} strokeWidth={2} /> : null}
      <span>{children}</span>
    </button>
  );
}

export function Status({
  tone,
  children,
}: {
  tone: "safe" | "warning" | "danger" | "neutral" | "info";
  children: ReactNode;
}) {
  return <span className={`status status--${tone}`}>{children}</span>;
}

export function Notice({
  tone,
  title,
  children,
  actions,
}: {
  tone: "safe" | "warning" | "danger" | "info";
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  const Icon =
    tone === "safe"
      ? CheckCircle2
      : tone === "warning"
        ? TriangleAlert
        : tone === "danger"
          ? AlertCircle
          : Info;
  return (
    <div className={`notice notice--${tone}`} role={tone === "danger" ? "alert" : "status"}>
      <Icon aria-hidden="true" size={19} strokeWidth={2} />
      <div className="notice__body">
        <strong>{title}</strong>
        {children ? <div className="notice__copy">{children}</div> : null}
      </div>
      {actions ? <div className="notice__actions">{actions}</div> : null}
    </div>
  );
}

export function EmptyState({
  icon: Icon = CircleDashed,
  title,
  description,
  action,
}: {
  icon?: LucideIcon;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <Icon aria-hidden="true" size={27} strokeWidth={1.7} />
      <h2>{title}</h2>
      <p>{description}</p>
      {action ? <div>{action}</div> : null}
    </div>
  );
}

export function LoadingScreen() {
  return (
    <div className="loading-layout" aria-label="Загрузка приложения" aria-busy="true">
      <div className="loading-layout__sidebar" />
      <div className="loading-layout__main">
        <div className="skeleton skeleton--title" />
        <div className="skeleton skeleton--line" />
        <div className="skeleton skeleton--hero" />
        <div className="loading-layout__columns">
          <div className="skeleton skeleton--panel" />
          <div className="skeleton skeleton--panel" />
        </div>
      </div>
    </div>
  );
}

export function UnavailableScreen({
  error,
  onRetry,
  title = "Рабочая база недоступна",
  guard = "Данные не заменены учебными. Отправка и изменение настроек через этот экран недоступны.",
  retryLabel = "Повторить подключение",
}: {
  error: string;
  onRetry: () => void;
  title?: string;
  guard?: string;
  retryLabel?: string;
}) {
  // A tab left open across a release fails here with the old bundle; a new
  // build is loaded instead of retrying the same incompatible request.
  useEffect(() => { void reloadIfNewBuild(); }, []);
  const retry = () => {
    void reloadIfNewBuild().then((reloading) => { if (!reloading) onRetry(); });
  };
  return (
    <main className="unavailable-layout">
      <div className="unavailable-card" role="alert">
        <span className="unavailable-card__icon"><AlertCircle aria-hidden="true" size={28} /></span>
        <h1>{title}</h1>
        <p>{error}</p>
        <p className="unavailable-card__guard">{guard}</p>
        <Button icon={RefreshCw} onClick={retry}>{retryLabel}</Button>
      </div>
    </main>
  );
}

export function Field({
  label,
  hint,
  error,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  htmlFor?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className="field">
      {htmlFor ? (
        <label className="field__label" htmlFor={htmlFor}>{label}</label>
      ) : (
        <span className="field__label">{label}</span>
      )}
      {children}
      {hint ? <span className="field__hint">{hint}</span> : null}
      {error ? <span className="field__error">{error}</span> : null}
    </div>
  );
}
